import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  useTheme,
  IconButton,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SignalTradingDaysCalendarDialog from "./Popup/SignalTradingDaysCalendarDialog";
import { englishOrdinal } from "./utility";

const METRIC_CARD_SX = {
  bgcolor: "background.paper",
  border: 1,
  borderColor: "divider",
  height: "100%",
};

const METRIC_CARD_CONTENT_SX = {
  minHeight: "120px",
  display: "flex",
  flexDirection: "column",
  px: { md: 1.5 },
  "&:last-child": { pb: 2 },
};

const METRIC_LABEL_SX = { fontSize: { md: "0.7rem" }, lineHeight: 1.2 };

const FOOTER_CAPTION_SX = { fontSize: { md: "0.65rem" }, lineHeight: 1.2 };


function MetricTile({ label, value, valueSx, footer }) {
  return (
    <Grid size={{ xs: 15, sm: 5, md: 3 }}>
      <Card sx={METRIC_CARD_SX}>
        <CardContent sx={METRIC_CARD_CONTENT_SX}>
          <Typography
            variant="body2"
            color="textSecondary"
            gutterBottom
            sx={METRIC_LABEL_SX}
          >
            {label}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              mt: 0.5,
              fontWeight: "bold",
              ...valueSx,
            }}
          >
            {value}
          </Typography>
          {footer ?? null}
        </CardContent>
      </Card>
    </Grid>
  );
}

const StrategyPerformanceMetrics = ({ metricData, strategyName = "" }) => {
  const theme = useTheme();
  const [calendarOpen, setCalendarOpen] = useState(false);
  const totalSignalSets = metricData?.signalSetTradingDays ?? 0;
  const signalCalendarYmds = metricData.signalCalendarYmds ?? [];
  const showCalendarIcon = signalCalendarYmds.length > 0;

  const getNumericValue = (valueStr) => {
    if (!valueStr || valueStr === "—" || valueStr === "0") return 0;
    const cleaned = valueStr.replace("%", "").trim();
    const num = parseFloat(cleaned);
    return isNaN(num) ? 0 : num;
  };

  const getValueColor = (valueStr) => {
    const numValue = getNumericValue(valueStr);
    if (numValue > 0) return theme.palette.success.main;
    if (numValue < 0) return theme.palette.error.main;
    return theme.palette.text.primary;
  };

  const valueMd = { xs: "1.1rem", md: "1rem" };

  const freqDaysNum = Number(metricData.signalSetFrequencyDays);
  const signalEveryDayLabel =
    Number.isFinite(freqDaysNum) && freqDaysNum > 0
      ? `Signal updated every ${englishOrdinal(freqDaysNum)} day`
      : "—";

  return (
    <Box sx={{ mb: 3 }}>
      <Grid container spacing={2} columns={15}>
        <MetricTile
          label="PERIOD"
          value={metricData.backtestPeriod}
          valueSx={{
            fontSize: { xs: "1.1rem", md: "0.95rem" },
            color: theme.palette.text.primary,
            lineHeight: 1.25,
            wordBreak: "break-word",
          }}
          footer={
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              {metricData.days}
            </Typography>
          }
        />

        <MetricTile
          label="TOTAL RETURN"
          value={metricData.totalReturn}
          valueSx={{
            fontSize: valueMd,
            color: getValueColor(metricData.totalReturn),
          }}
        />

        <MetricTile
          label="SHARPE RATIO"
          value={metricData.sharpeRatio}
          valueSx={{
            fontSize: valueMd,
            color: getValueColor(metricData.sharpeRatio),
          }}
          footer={
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ mt: "auto", pt: 0.5, ...FOOTER_CAPTION_SX }}
            >
              Risk-adjusted return
            </Typography>
          }
        />

        <MetricTile
          label="MAX DRAWDOWN"
          value={metricData.maxDrawdown}
          valueSx={{
            fontSize: valueMd,
            color: getValueColor(metricData.maxDrawdown),
          }}
          footer={
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ mt: "auto", pt: 0.5, ...FOOTER_CAPTION_SX }}
            >
              {metricData.drawdownDuration}
            </Typography>
          }
        />

        <MetricTile
          label="SIGNAL SETS"
          value={totalSignalSets}
          valueSx={{
            fontSize: valueMd,
            color: theme.palette.text.primary,
          }}
          footer={
            <Box
              sx={{
                mt: "auto",
                pt: 0.5,
                display: "flex",
                alignItems: "center",
                gap: 0.25,
                minHeight: 28,
              }}
            >
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ flex: 1, ...FOOTER_CAPTION_SX }}
              >
                {signalEveryDayLabel}
              </Typography>
              {showCalendarIcon ? (
                <IconButton
                  size="small"
                  aria-label="View calendar of all signal dates (US Eastern)"
                  onClick={() => setCalendarOpen(true)}
                  sx={{ ml: "auto", flexShrink: 0 }}
                >
                  <CalendarMonthIcon fontSize="small" />
                </IconButton>
              ) : null}
            </Box>
          }
        />
      </Grid>

      {showCalendarIcon ? (
        <SignalTradingDaysCalendarDialog
          open={calendarOpen}
          onClose={() => setCalendarOpen(false)}
          highlightedYmds={signalCalendarYmds}
          signalsByCalendarYmd={metricData.signalSetsByCalendarYmd ?? {}}
          periodMinYmd={metricData.signalCalendarPeriodStartYmd}
          periodMaxYmd={metricData.signalCalendarPeriodEndYmd}
          strategyName={strategyName}
        />
      ) : null}
    </Box>
  );
};

export default StrategyPerformanceMetrics;
